import axios from 'axios'
import api from '../index'

// ==================== DTK 抖音数据接口（v5，douyin-api2） ====================
// 新接口为异步任务制：POST /api/v1/parse 提交链接，?wait=30 尽量同步返回；
// 超时则拿 task_id 轮询 GET /api/v1/tasks/{task_id} 直到完成。
// 响应信封统一为 { success, data, error, meta }，鉴权使用 X-API-Key 请求头。

const dtkApi = axios.create({
  baseURL: '/douyin-api',
  timeout: 1000 * 60,
  headers: {
    'X-API-Key': import.meta.env.VITE_DTK_X_API_KEY,
  },
})

export interface DtkError {
  code: string
  message: string
  details?: Record<string, any>
  retry_after?: number | null
  retryable?: boolean
}

export interface DtkResponse<T = any> {
  success: boolean
  data: T | null
  error: DtkError | null
  meta: Record<string, any>
}

export interface DtkImage {
  url: string
  urls: string[]
  width?: number | null
  height?: number | null
}

export interface DtkVideoStream {
  url: string
  urls: string[]
  width?: number | null
  height?: number | null
  bitrate?: number | null
  format?: string | null
  size_bytes?: number | null
  watermark?: boolean
}

export interface DtkMedia {
  covers: DtkImage[]
  video: DtkVideoStream | null
  streams: DtkVideoStream[]
  images: DtkImage[]
}

export interface DtkAuthorStats {
  follower_count?: number | null
  following_count?: number | null
  content_count?: number | null
  total_digg?: number | null
}

export interface DtkAuthor {
  platform: string
  uid: string
  sec_uid?: string | null
  unique_id?: string | null
  nickname: string
  signature?: string | null
  avatar?: DtkImage | null
  stats?: DtkAuthorStats | null
}

export interface DtkContentStats {
  play_count?: number | null
  digg_count?: number | null
  comment_count?: number | null
  share_count?: number | null
  collect_count?: number | null
}

/** DTK 归一化作品记录 */
export interface DtkContent {
  platform: string
  content_id: string
  kind: string
  web_url: string
  /** 文案第一行 */
  title: string
  /** 完整文案 */
  description: string
  created_at?: string | null
  duration_ms?: number | null
  author: DtkAuthor
  stats: DtkContentStats
  media: DtkMedia
  tags: string[]
}

/** 任务信息：提交后立即返回，或 wait 超时后返回；完成时结果放在 data 字段 */
export interface DtkTask {
  task_id: string
  state: 'queued' | 'running' | 'done' | 'failed'
  endpoint?: string
  created_at?: string
  finished_at?: string | null
  /** state 为 done 时的任务结果（归一化作品数据） */
  data?: DtkContent
  error?: DtkError | null
}

export type ParseDouYinLinkResponse = DtkResponse<DtkContent>

export interface UserItem {
  id: number
  name: string
  douyin_name: string
  create_at: string
}

export interface UserListResponse {
  code: number
  msg: string
  data: UserItem[]
}

/** 轮询任务直到完成，返回归一化作品数据 */
async function dtkPollTask(taskId: string): Promise<ParseDouYinLinkResponse> {
  // wait=30 已等过一次，这里最多再轮询 20 次 × 2 秒
  for (let i = 0; i < 20; i++) {
    await new Promise(resolve => setTimeout(resolve, 2000))
    const res = await dtkApi.get<DtkResponse<DtkTask>>(`/v1/tasks/${taskId}`, { params: { lang: 'zh' } })
    const body = res.data
    if (!body.success) {
      throw new Error(body.error?.message || '抖音解析任务失败')
    }
    const task = body.data
    if (task?.state === 'done') {
      return { ...body, data: task.data ?? null } as ParseDouYinLinkResponse
    }
    if (task?.state === 'failed') {
      throw new Error(task.error?.message || body.error?.message || '抖音解析任务失败')
    }
  }
  throw new Error('抖音解析超时，请稍后重试')
}

export default {
  // 获取媒体列表
  get_video_list: (data: { page: number, size: number }) => api.get('/getcomments', {
    params: data,
    // baseURL:'/api'
  }),

  // 获取所有用户
  get_user_list: () => api.get<UserListResponse>('/getUserList', {
    //  baseURL:'/api'
  }),

  // 保存数据
  saveHandData: (data: { who: string, user_id: string, video_desc: string, raw_desc: string, comments: string, cover: string, comments_count?: number }) => api.post('/saveComment', data),

  // 解析抖音链接废弃
  parseDouYinLinkOld: (params: { url: string }) => {
    return api.get('/comment/parseDouYinLink', {
      params,
    })
  },

  // 解析抖音链接（DTK v5：异步任务制，?wait=30 同步等待，超时自动轮询任务）
  parseDouYinLink: async (params: { url: string }): Promise<ParseDouYinLinkResponse> => {
    const res = await dtkApi.post<DtkResponse<DtkContent | DtkTask>>('/v1/parse', { url: params.url }, {
      params: { wait: 30, lang: 'zh' },
    })
    const body = res.data
    if (!body.success) {
      throw new Error(body.error?.message || '抖音链接解析失败')
    }
    const data = body.data as DtkTask | DtkContent | null
    // wait 超时（202）：data 里是任务信息，转轮询；防御性处理已完成任务
    if (data && 'task_id' in data && 'state' in data) {
      if (data.state === 'done') {
        return { ...body, data: data.data ?? null } as ParseDouYinLinkResponse
      }
      return dtkPollTask(data.task_id)
    }
    return body as ParseDouYinLinkResponse
  },

  // 获取视频评论详情
  get_comment_detail: (params: { id: string }) => {
    return api.get('/comment/detail', {
      params,
    })
  },

  // 生成评论
  generate_comments: (data: { prompt: string }) => api.post('/generateComments', data),
}
